import type { ApiResult } from './types';
import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import {
  ensureExemptSubFileCategoryIdsLoaded,
  fileCategoryExemptFromSubFileCategory,
  normalizeFileCategoryLabel,
} from '../utils/subFileCategoryRules';

export type CaseRequestCategoryAllocationRow = {
  id: string;
  fileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryId: string;
  subFileCategoryName: string;
  supervisorId: string;
  supervisorDisplayName: string;
  supervisorTitle: string;
  stateAttorneyId: string;
  stateAttorneyDisplayName: string;
  stateAttorneyTitle: string;
};

export type CategoryAllocationUserRow = {
  caseRequestCategoryAllocationUserId: string;
  userId: string;
  userDisplayName: string;
  userTitle: string;
  userRoles: string;
  /** Set when users are merged from multiple category allocations (supervisor hidden). */
  requestCategoryAllocationId?: string;
};

type ApiAllocationRow = {
  id?: string;
  request_category_allocation_id?: string;
  file_category_id?: string;
  file_category_name?: string;
  sub_file_category_id?: string;
  sub_file_category_name?: string;
  supervisor_id?: string;
  supervisor_display_name?: string;
  supervisor_title?: string;
  state_attorney_id?: string;
  state_attorney_display_name?: string;
  state_attorney_title?: string;
};

type ApiAllocationUserRow = {
  case_request_category_allocation_user_id?: string;
  user_id?: string;
  user_display_name?: string;
  user_title?: string;
  user_roles?: string;
  roles?: string;
};

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

function normalizeAllocationRows(data: unknown): CaseRequestCategoryAllocationRow[] {
  if (!Array.isArray(data)) return [];
  const out: CaseRequestCategoryAllocationRow[] = [];
  for (const item of data) {
    const r = (item && typeof item === 'object' ? (item as ApiAllocationRow) : {}) as ApiAllocationRow;
    const id = str(r.id) || str(r.request_category_allocation_id);
    const fileCategoryId = str(r.file_category_id);
    const subFileCategoryId = str(r.sub_file_category_id);
    const supervisorId = str(r.supervisor_id);
    if (!id || !fileCategoryId || !supervisorId) continue;
    out.push({
      id,
      fileCategoryId,
      fileCategoryName: str(r.file_category_name),
      subFileCategoryId,
      subFileCategoryName: str(r.sub_file_category_name),
      supervisorId,
      supervisorDisplayName: str(r.supervisor_display_name),
      supervisorTitle: str(r.supervisor_title),
      stateAttorneyId: str(r.state_attorney_id),
      stateAttorneyDisplayName: str(r.state_attorney_display_name),
      stateAttorneyTitle: str(r.state_attorney_title),
    });
  }
  return out;
}

function normalizeAllocationUsers(data: unknown): CategoryAllocationUserRow[] {
  if (!Array.isArray(data)) return [];
  const out: CategoryAllocationUserRow[] = [];
  for (const item of data) {
    const r = (item && typeof item === 'object' ? (item as ApiAllocationUserRow) : {}) as ApiAllocationUserRow;
    const id = str(r.case_request_category_allocation_user_id);
    const userId = str(r.user_id);
    if (!id || !userId) continue;
    out.push({
      caseRequestCategoryAllocationUserId: id,
      userId,
      userDisplayName: str(r.user_display_name),
      userTitle: str(r.user_title),
      userRoles: str(r.user_roles) || str(r.roles),
    });
  }
  return out;
}

/** CMS-CCA-1 — Get category allocations by file category (and optional sub file category). */
export async function getCategoryAllocations(input: {
  fileCategoryId: string;
  subFileCategoryId?: string;
  fileCategoryName?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<CaseRequestCategoryAllocationRow[]>> {
  await ensureExemptSubFileCategoryIdsLoaded();

  const fileCategoryId = input.fileCategoryId.trim();
  const subFileCategoryId = (input.subFileCategoryId ?? '').trim();
  const fileCategoryName = normalizeFileCategoryLabel(input.fileCategoryName);
  if (!fileCategoryId) return { success: true, data: [] };

  const exemptFromSubCategory = fileCategoryExemptFromSubFileCategory({
    id: fileCategoryId,
    name: fileCategoryName,
  });
  if (!subFileCategoryId && !exemptFromSubCategory) return { success: true, data: [] };

  const qs = new URLSearchParams({ fileCategoryId });
  if (subFileCategoryId) qs.set('subFileCategoryId', subFileCategoryId);
  const res = await caseApiRequest<unknown>(`/category-allocations?${qs.toString()}`, { signal: input.signal });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeAllocationRows(res.data) };
}

/** CMS-CCA-2 — Get active users linked to a specific category allocation. */
export async function getCategoryAllocationUsers(input: {
  requestCategoryAllocationId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<CategoryAllocationUserRow[]>> {
  const id = input.requestCategoryAllocationId.trim();
  if (!id) return { success: true, data: [] };

  const qs = new URLSearchParams({ requestCategoryAllocationId: id });
  const res = await caseApiRequest<unknown>(`/category-allocations/users?${qs.toString()}`, { signal: input.signal });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeAllocationUsers(res.data) };
}

/** CMS-CCA-3 — Assign a user to a workflow task and advance workflow. */
export async function assignCategoryAllocationUser(body: {
  caseRequestCategoryAllocationUserId: string;
  userId: string;
  workflowInstanceStageUserTaskId: string;
  supervisorId?: string;
  stateAttorneyId?: string;
}): Promise<ApiResult<void>> {
  const payload: Record<string, string> = {
    caseRequestCategoryAllocationUserId: body.caseRequestCategoryAllocationUserId,
    userId: body.userId,
    workflowInstanceStageUserTaskId: body.workflowInstanceStageUserTaskId,
  };
  const supervisorId = body.supervisorId?.trim();
  const stateAttorneyId = body.stateAttorneyId?.trim();
  if (supervisorId) payload.supervisorId = supervisorId;
  if (stateAttorneyId) payload.stateAttorneyId = stateAttorneyId;

  const res = await caseApiRequest<unknown>('/category-allocations/users/assign', {
    method: 'POST',
    body: payload,
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}

function parseManualAllocationFlag(data: unknown): boolean | null {
  if (!data || typeof data !== 'object') return null;
  const r = data as Record<string, unknown>;
  if (typeof r.is_manual_allocation === 'boolean') return r.is_manual_allocation;
  if (typeof r.isManualAllocation === 'boolean') return r.isManualAllocation;
  return null;
}

/** CMS-CCA-11 — Get manual allocation mode flag. */
export async function getManualAllocationMode(input?: {
  signal?: AbortSignal;
}): Promise<ApiResult<{ isManualAllocation: boolean }>> {
  const res = await caseApiRequest<unknown>('/category-allocations/manual-mode', {
    signal: input?.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  const isManualAllocation = parseManualAllocationFlag(res.data);
  if (isManualAllocation === null) return { success: false, message: 'Invalid manual allocation mode response.' };
  return { success: true, data: { isManualAllocation } };
}

/**
 * BE returns HTTP 200 / api_status SUCCESS even when some tasks were skipped, e.g.:
 * { "data": { "response_result": true, "message": "Round Robin Allocation can not be
 * performed for some tasks due to incomplete user categorization." }, "api_status": { "status": "SUCCESS" } }
 * `isDataPayloadSuccess` only looks at `data.status`, so this shape passes through as success —
 * we separately surface `data.message` as a non-blocking warning for the caller to toast.
 */
function parseRoundRobinWarningMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const msg = (data as Record<string, unknown>).message;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
}

/** CMS-CCA-4 — Round-robin assign a list of case requests. */
export async function roundRobinAssignUsers(body: {
  caseRequestIds: string[];
  isManualAllocation: boolean;
}): Promise<ApiResult<{ warningMessage: string | null }>> {
  const res = await caseApiRequest<unknown>('/category-allocations/users/round-robin-assign', {
    method: 'POST',
    body: {
      case_request_ids: body.caseRequestIds,
      is_manual_allocation: body.isManualAllocation,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: { warningMessage: parseRoundRobinWarningMessage(res.data) } };
}